import { trpc } from '@/lib/trpc/trpc';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { CalendarPicker } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';

interface CalendarEventFormProps {
  eventId?: string;
  title?: string;
  startDate?: Date | undefined | null;
  endDate?: Date | undefined | null;
  comment?: string;
  parentShow?: (open: boolean) => void;
}

export default function CalendarEventForm({
  ...props
}: CalendarEventFormProps) {
  const trpcUtils = trpc.useUtils();
  const { toast } = useToast();

  const addCalendarEvent = trpc.Booking.CreateEvent.useMutation({
    onSuccess: async () => {
      toast({
        title: 'Event Added Successfully',
        description: 'Successfully created event',
      });
      await trpcUtils.Booking.invalidate();
      if (props.parentShow) {
        props.parentShow(false);
      }
    },
    onError: async (error) => {
      toast({
        title: 'Error Adding Event',
        description: error.message,
        variant: 'destructive',
      });
      await trpcUtils.Booking.invalidate();
    },
  });

  const updateCalendarEvent = trpc.Booking.UpdateEvent.useMutation({
    onSuccess: async () => {
      toast({
        title: 'Event Updated Successfully',
        description: 'Successfully updated event',
      });
      await trpcUtils.Booking.invalidate();
      if (props.parentShow) {
        props.parentShow(false);
      }
    },
    onError: async (error) => {
      toast({
        title: 'Error Updating Event',
        description: error.message,
        variant: 'destructive',
      });
      await trpcUtils.Booking.invalidate();
    },
  });

  const calendarEventFormSchema = z.object({
    startDate: z.date(),
    endDate: z.date(),
    title: z.string(),
    comment: z.string().optional(),
  });

  const calendarEventForm = useForm<z.infer<typeof calendarEventFormSchema>>({
    resolver: zodResolver(calendarEventFormSchema),
    defaultValues: {
      startDate: props.startDate || undefined,
      endDate: props.endDate || undefined,
      title: props.title,
      comment: props.comment || undefined,
    },
  });

  const calendarEventFormHandler = (
    values: z.infer<typeof calendarEventFormSchema>
  ) => {
    if (props.eventId) {
      updateCalendarEvent.mutate({
        eventId: props.eventId,
        startDate: values.startDate,
        endDate: values.endDate,
        title: values.title,
        comment: values.comment || '',
      });
    } else {
      addCalendarEvent.mutate({
        startDate: values.startDate,
        endDate: values.endDate,
        title: values.title,
        comment: values.comment || '',
      });
    }
  };

  return (
    <Form {...calendarEventForm}>
      <form
        onSubmit={calendarEventForm.handleSubmit(calendarEventFormHandler)}
        onReset={() => calendarEventForm.reset()}
      >
        <div className='grid grid-cols-1 gap-3 py-4'>
          <FormField
            control={calendarEventForm.control}
            name='title'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Event Title</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={calendarEventForm.control}
            name='startDate'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel className='p-2'>Start Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'w-[240px] pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-0' align='start'>
                      <CalendarPicker
                        mode='single'
                        selected={field.value}
                        onSelect={field.onChange}
                      />
                    </PopoverContent>
                  </Popover>
                </FormItem>
              );
            }}
          />
          <FormField
            control={calendarEventForm.control}
            name='endDate'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel className='p-3'>End Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'w-[240px] pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-0' align='start'>
                      <CalendarPicker
                        mode='single'
                        selected={field.value}
                        onSelect={field.onChange}
                      />
                    </PopoverContent>
                  </Popover>
                </FormItem>
              );
            }}
          />
          <FormField
            control={calendarEventForm.control}
            name='comment'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comment?</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <br />
        <FormMessage />
        <div className='space-x-3'>
          <Button type='submit'>Save Event</Button>
          <Button type='reset' variant={'destructive'}>
            Clear
          </Button>
        </div>
      </form>
    </Form>
  );
}
